import React, { createContext, useState, useEffect } from 'react';
import { useAppConfigContext } from '../AppConfigProvider';
import { IApiConfig, useApi } from '../../hooks/useApi';
import { UniversalAuthUser, useUser } from '../../hooks/useUser';
import {
    LicenseManagementContextProps,
    ListUsersResponse,
    Seats,
    User,
} from './LicenseManagementContext.types';
import { useAccessControl } from '../../hooks/useAccessControl';
import {
    AUTHOR_ROLE_NAME,
    ROLES_WITH_QS_ACCESS,
} from '../../components/Settings/User/roles';

type LicenseManagementContextInternalProps = LicenseManagementContextProps & {
    init: () => void;
};

export const LicenseManagementContext =
    createContext<LicenseManagementContextInternalProps>({
        loading: false,
        loaded: false,
        init: () => {},
        addUsers: () => {},
        deleteUser: () => {},
    });

export const useLicenseManagementContext: () => LicenseManagementContextProps =
    () => {
        const ctx = React.useContext(LicenseManagementContext);
        React.useEffect(() => {
            ctx.init();
        }, [ctx]);

        return ctx as LicenseManagementContextProps;
    };

export const LicenseManagementProvider: React.FunctionComponent = ({
    children,
}) => {
    const { api } = useAppConfigContext();
    const user = useUser();
    const access = useAccessControl();

    const [users, setUsers] = useState<User[]>([]);
    const [loaded, setLoaded] = useState(false);

    const [shouldRun, setShouldRun] = React.useState(false);

    const tenantId = user?.isUniversalUser()
        ? (user as UniversalAuthUser).tenantId
        : null;
    const initialized = React.useRef(false);

    const getUsersApiConfig = React.useMemo<IApiConfig>(
        () => ({
            url: encodeURI(
                `${api!.userManagement}/v2/tenants/${tenantId}/users`,
            ),

            useAuth: true,
            executeType: 'manual',
            method: 'GET',
        }),
        [api, tenantId],
    );

    const {
        data: usersData,
        loading: usersLoading,
        error: usersError,
        execute: fetchUsers,
    } = useApi<ListUsersResponse>(getUsersApiConfig);

    const init = React.useMemo(
        () => () => {
            setShouldRun(true);
        },
        [],
    );

    React.useEffect(() => {
        if (initialized.current) {
            return;
        }
        if (tenantId && shouldRun && access['developer/settings/users'].ok) {
            initialized.current = true;
            fetchUsers();
        }
    }, [fetchUsers, shouldRun, tenantId, access]);

    useEffect(() => {
        if (usersData?.users) {
            setUsers(usersData.users);
            setLoaded(true);
        }
    }, [usersData]);

    const quotas = React.useMemo(() => usersData?.quotas as Seats, [usersData]);

    const seatsUsed = React.useMemo(
        () =>
            users &&
            ({
                reader:
                    users.filter(
                        user =>
                            user.active &&
                            !(user.roles || [])
                                .map(r => r.name)
                                .includes(AUTHOR_ROLE_NAME) &&
                            (user.roles || []).some(r =>
                                ROLES_WITH_QS_ACCESS.includes(r.name as any),
                            ),
                    ).length || 0,
                author:
                    users.filter(
                        user =>
                            user.active &&
                            (user.roles || [])
                                .map(r => r.name)
                                .includes(AUTHOR_ROLE_NAME),
                    ).length || 0,
            } as Seats),
        [users],
    );

    const doRefresh = React.useCallback(() => {
        fetchUsers();
    }, [fetchUsers]);

    const replaceUserData = React.useMemo(
        () => (newUser: User) => {
            setUsers(prevUsers =>
                prevUsers.map(prevUser =>
                    prevUser.userId === newUser.userId ? newUser : prevUser,
                ),
            );
        },
        [],
    );

    const addUsers = React.useMemo(
        () => (users: User[]) => {
            setUsers(prevUsers => [...prevUsers, ...users]);
        },
        [],
    );

    const deleteUser = React.useMemo(
        () => (userId: string) => {
            setUsers(prevUsers => prevUsers.filter(u => u.userId !== userId));
        },
        [],
    );
    return (
        <LicenseManagementContext.Provider
            value={{
                loading: usersLoading,
                loaded,
                users,
                quotas,
                seatsUsed,
                error: usersError,
                refresh: doRefresh,
                replaceUserData,
                init,
                addUsers,
                deleteUser,
            }}
        >
            {children}
        </LicenseManagementContext.Provider>
    );
};

export default LicenseManagementProvider;
